/**
 * HttpFsDirectory — a read-only folder served over HTTP.
 *
 * This is what powers "try it with sample photos": the demo shoot ships with the
 * app, so a first-time visitor can cull a real set before deciding whether to
 * hand over a folder. Read-only by design — demo photos are never written back.
 */

import { FileMissingError, splitPath, type FsDirectory, type FsEntry, type FsFile } from './fs-port';

export interface HttpManifestEntry {
  path: string;
  size: number;
  modified: number;
}

export interface HttpManifest {
  name: string;
  files: HttpManifestEntry[];
}

class HttpFsFile implements FsFile {
  private cache: Uint8Array | null = null;

  constructor(
    readonly name: string,
    readonly size: number,
    readonly lastModified: number,
    private readonly url: string,
  ) {}

  async bytes(): Promise<Uint8Array> {
    if (!this.cache) {
      const response = await fetch(this.url);
      if (!response.ok) throw new FileMissingError(this.name);
      this.cache = new Uint8Array(await response.arrayBuffer());
    }
    return this.cache;
  }

  async blob(): Promise<Blob> {
    const response = await fetch(this.url);
    if (!response.ok) throw new FileMissingError(this.name);
    return response.blob();
  }
}

export class HttpFsDirectory implements FsDirectory {
  constructor(
    readonly name: string,
    private readonly baseUrl: string,
    private readonly manifest: HttpManifest,
    private readonly prefix = '',
  ) {}

  static async load(baseUrl: string, manifestUrl: string): Promise<HttpFsDirectory> {
    const response = await fetch(manifestUrl);
    if (!response.ok) throw new Error('Sample photos could not be loaded.');
    const manifest = (await response.json()) as HttpManifest;
    return new HttpFsDirectory(manifest.name, baseUrl.replace(/\/$/, ''), manifest);
  }

  private within(): HttpManifestEntry[] {
    if (!this.prefix) return this.manifest.files;
    return this.manifest.files
      .filter((file) => file.path.startsWith(`${this.prefix}/`))
      .map((file) => ({ ...file, path: file.path.slice(this.prefix.length + 1) }));
  }

  async list(): Promise<FsEntry[]> {
    const seen = new Set<string>();
    const entries: FsEntry[] = [];

    for (const file of this.within()) {
      const slash = file.path.indexOf('/');
      if (slash === -1) {
        entries.push({ kind: 'file', name: file.path });
      } else {
        const dir = file.path.slice(0, slash);
        if (!seen.has(dir)) {
          seen.add(dir);
          entries.push({ kind: 'directory', name: dir });
        }
      }
    }
    return entries;
  }

  async directory(name: string): Promise<FsDirectory> {
    return new HttpFsDirectory(
      name,
      this.baseUrl,
      this.manifest,
      this.prefix ? `${this.prefix}/${name}` : name,
    );
  }

  async file(relPath: string): Promise<FsFile> {
    const full = this.prefix ? `${this.prefix}/${relPath}` : relPath;
    const entry = this.manifest.files.find((file) => file.path === full);
    if (!entry) throw new FileMissingError(relPath);

    const { filename } = splitPath(relPath);
    return new HttpFsFile(filename, entry.size, entry.modified, `${this.baseUrl}/${full}`);
  }

  async has(relPath: string): Promise<boolean> {
    const full = this.prefix ? `${this.prefix}/${relPath}` : relPath;
    return this.manifest.files.some((file) => file.path === full);
  }

  /** Parameters are declared to match FsDirectory, even though nothing uses them. */
  async write(_name: string, _bytes: Uint8Array): Promise<void> {
    throw new Error('The sample shoot is read-only.');
  }
}
